import type { ProductColor } from '@aloft/toolkit-shared';
import { propertyHasCustomManualAdjustments } from '@aloft/toolkit-shared';
import { Button, Flex, Typography } from '@aloft/toolkit-sui';

import { upperCase } from 'lodash';

import { GuidedTourTooltip } from '../../../components/GuidedTour/GuidedTourTooltip.tsx';
import { IndicatedValueInformationIcon } from '../../../components/IndicatedValueInformationIcon.tsx';
import { selectCompType } from '../../../redux/features/selectors/orderSelectors.ts';
import { selectManualAdjustmentsById } from '../../../redux/features/selectors/propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectFirstOrFewerCompID } from '../../../redux/features/selectors/selectFirstOrFewerCompID.ts';
import { selectPropertyValueDisplayById } from '../../../redux/features/selectors/selectPropertyValue.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { PLACEHOLDER } from '../consts.ts';

type HeaderProps = {
  id: string;
  onModeToggle: () => void;
  editMode: 'objective' | 'subjective';
  disableEdit: boolean;
  hideEdit?: boolean;
  indicatedValueTitle?: string;
  backgroundColor?: ProductColor;
  textColor?: ProductColor;
};

export const Header = ({
  id,
  onModeToggle,
  editMode,
  disableEdit,
  hideEdit,
  indicatedValueTitle,
  backgroundColor,
  textColor,
}: HeaderProps) => {
  const secondOrFewerCompID = useAppSelector(selectFirstOrFewerCompID);
  const { id: _id, ...manualAdjustments } =
    useAppSelector((state) => selectManualAdjustmentsById(state, id)) ?? {};

  const propertyValue = useAppSelector((state) => selectPropertyValueDisplayById(state, id));
  const compType = useAppSelector((state) => selectCompType(state, id));
  let compBackgroundColor = backgroundColor;
  let compTextColor = textColor;
  let compBadgeBackgroundColor = backgroundColor;
  if (compType === 'aiv') {
    compBackgroundColor = 'aivLight';
    compTextColor = 'aivText';
    compBadgeBackgroundColor = 'aiv';
  } else if (compType === 'arv') {
    compBackgroundColor = 'arvLight';
    compTextColor = 'arvText';
    compBadgeBackgroundColor = 'arv';
  }

  return (
    <Flex
      backgroundColor={compBackgroundColor ?? 'alertInfoLight'}
      alignItems="center"
      justifyContent="space-between"
      gap="0.5rem"
      padding="0.5rem"
      height="2.25rem"
    >
      <Flex gap=".5rem" alignItems="center">
        <Typography
          color={textColor ?? 'textPrimary'}
          variant="h4"
        >{`${indicatedValueTitle ?? 'Indicated Cost Value'}: ${propertyValue ?? PLACEHOLDER}`}</Typography>
        <IndicatedValueInformationIcon
          propertyId={id}
          compIsManuallyAdjusted={propertyHasCustomManualAdjustments(manualAdjustments)}
          color={textColor ?? 'textPrimary'}
        />
      </Flex>
      {!hideEdit && !compType && editMode === 'subjective' && (
        <GuidedTourTooltip
          text="If you need to add, remove or edit property features, click on the ‘Edit’ button on any property."
          tourID="analysisTour"
          tourIndex={2}
          displayCondition={id === secondOrFewerCompID}
          placement="bottom"
        >
          <Button
            variant="secondary"
            size="sm"
            onClick={onModeToggle}
            text="Edit"
            onMouseDown={(e) => {
              // Prevent focus change
              e.preventDefault();
            }}
            disabled={disableEdit}
          />
        </GuidedTourTooltip>
      )}
      {compType && (
        <Flex
          backgroundColor={compBadgeBackgroundColor}
          __dangerDoNotUseSx={{
            padding: '2px 6px',
            borderRadius: '4px',
          }}
        >
          <Typography variant="body2" color={compTextColor}>
            {upperCase(compType)}
          </Typography>
        </Flex>
      )}
    </Flex>
  );
};
